import React, { useEffect, useRef, useState } from "react";
import {
   AnimatePresence,
   motion,
   useInView,
   useMotionValue,
   useReducedMotion,
   useScroll,
   useSpring,
   useTransform,
} from "framer-motion";
import { Check, ChevronDown, Plus, ShoppingBag } from "lucide-react";

import kitchenPhoto from "../../../assets/admin/landing/kitchen-sm.webp";
import KitchenTicket, { TicketPrinter } from "./KitchenTicket";
import { BUSINESS_TYPES, SHOP, dish, upcomingDays, weekdayNow } from "./data";
import {
   CONTACT_MAIL,
   EASE,
   Em,
   MaskLines,
   PillButton,
   euro,
   scrollToTarget,
   useIntroDone,
} from "./primitives";

/* ------------------------------------------------------------------ */
/*  Telefoon die zelf een bestelling plaatst                           */
/* ------------------------------------------------------------------ */

const HERO_DISHES = ["kroket", "stoof", "steak"].map(dish);
const [D1, D2] = HERO_DISHES;
// De gast bestelt vandaag voor overmorgen: zo gebruiken de meeste zaken het
const DAYS = upcomingDays(4);
const PICK = DAYS[2];
const HERO_ORDER = {
   number: "0142",
   mode: "Afhalen",
   slot: `${PICK.short} 18:30`,
   time: `${weekdayNow()} 21:14`,
   items: [
      { name: D1.name, qty: 1, price: D1.price },
      { name: D2.name, qty: 1, price: D2.price },
   ],
   total: D1.price + D2.price,
};

// fase → hoe lang ze duurt (ms)
// 0 rust · 1 gerecht 1 · 2 gerecht 2 · 3 mandje open · 4 betalen · 5 betaald + bon
const PHASE_MS = [1300, 1000, 1200, 1500, 800, 4200];

const TapPulse = ({ on }) => (
   <AnimatePresence>
      {on && (
         <motion.span
            className="pointer-events-none absolute inset-0 rounded-full border-2 border-lp-blue"
            initial={{ scale: 0.6, opacity: 1 }}
            animate={{ scale: 2.2, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
         />
      )}
   </AnimatePresence>
);

function HeroPhone({ phase }) {
   const inCart = (i) => (i === 0 && phase >= 1) || (i === 1 && phase >= 2);
   const count = phase >= 2 ? 2 : phase >= 1 ? 1 : 0;
   const total = (phase >= 1 ? D1.price : 0) + (phase >= 2 ? D2.price : 0);

   return (
      <div className="relative h-[600px] w-[292px] rounded-[48px] bg-lp-ink p-[10px] shadow-[0_60px_80px_-40px_rgba(18,17,16,0.55),inset_0_0_0_1.5px_rgba(255,255,255,0.08)]">
         <div className="relative h-full w-full overflow-hidden rounded-[38px] bg-lp-card">
            {/* notch + statusbalk */}
            <div className="absolute left-1/2 top-2.5 z-30 h-[22px] w-[86px] -translate-x-1/2 rounded-full bg-lp-ink" />
            <div className="flex items-center justify-between px-6 pt-3 text-[11px] font-semibold text-lp-ink">
               <span>21:14</span>
               <span className="flex items-center gap-1">
                  <span className="h-2 w-3 rounded-[2px] bg-lp-ink/80" />
                  <span className="h-2 w-4 rounded-[3px] border border-lp-ink/70" />
               </span>
            </div>

            {/* shopkop */}
            <div className="mt-6 flex items-center gap-3 px-5">
               <span className="flex h-10 w-10 items-center justify-center rounded-full bg-lp-ink font-editorial text-xl italic text-lp-paper">
                  {SHOP.initial}
               </span>
               <div className="leading-tight">
                  <p className="text-[15px] font-semibold tracking-tight text-lp-ink">{SHOP.name}</p>
                  <p className="text-[11px] text-lp-muted">Bestel vooraf · afhalen of levering</p>
               </div>
            </div>

            <div className="mt-4 flex gap-1.5 px-5">
               {["Hoofdgerechten", "Voorgerechten", "Desserts"].map((c, i) => (
                  <span
                     key={c}
                     className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-medium ${
                        i === 0 ? "bg-lp-ink text-lp-paper" : "bg-lp-ink/[0.06] text-lp-ink/60"
                     }`}
                  >
                     {c}
                  </span>
               ))}
            </div>

            {/* gerechten */}
            <div className="mt-4 space-y-2.5 px-4">
               {HERO_DISHES.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_1px_0_rgba(18,17,16,0.05)]">
                     <img src={d.thumb} alt="" width={56} height={56} className="!h-14 !w-14 flex-shrink-0 rounded-xl object-cover" />
                     <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-lp-ink">{d.name}</p>
                        <p className="truncate text-[10.5px] text-lp-muted">{d.desc}</p>
                        <p className="mt-1 text-[12px] font-semibold tabular-nums text-lp-ink">{euro(d.price)}</p>
                     </div>
                     <span
                        className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                           inCart(i) ? "bg-lp-blue text-white" : "bg-lp-ink/[0.06] text-lp-ink"
                        }`}
                     >
                        {inCart(i) ? <Check size={15} strokeWidth={3} /> : <Plus size={15} strokeWidth={2.5} />}
                        <TapPulse on={phase === i + 1} />
                     </span>
                  </div>
               ))}
            </div>

            {/* mandje-balk */}
            <motion.div
               initial={false}
               animate={{ y: count > 0 && phase < 3 ? 0 : 90 }}
               transition={{ duration: 0.55, ease: EASE }}
               className="absolute inset-x-4 bottom-5 flex items-center justify-between rounded-2xl bg-lp-ink px-4 py-3.5 text-lp-paper"
            >
               <span className="flex items-center gap-2 text-[13px] font-medium">
                  <ShoppingBag size={15} />
                  Mandje
                  <motion.span
                     key={count}
                     initial={{ scale: 1.6 }}
                     animate={{ scale: 1 }}
                     className="flex h-5 min-w-5 items-center justify-center rounded-full bg-lp-blue px-1.5 text-[10px] font-bold"
                  >
                     {count}
                  </motion.span>
               </span>
               <span className="text-[13px] font-semibold tabular-nums">{euro(total)}</span>
            </motion.div>

            {/* afrekenen */}
            <AnimatePresence>
               {phase >= 3 && phase < 5 && (
                  <motion.div
                     className="absolute inset-0 z-10 flex flex-col justify-end bg-lp-ink/30"
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     exit={{ opacity: 0 }}
                  >
                     <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="rounded-t-[28px] bg-lp-card px-5 pb-6 pt-3"
                     >
                        <span className="mx-auto block h-1 w-10 rounded-full bg-lp-ink/15" />
                        <p className="mt-4 text-[15px] font-semibold text-lp-ink">Kies je afhaalmoment</p>
                        <div className="mt-3 grid grid-cols-4 gap-1.5">
                           {DAYS.map((d) => (
                              <span
                                 key={d.i}
                                 className={`rounded-xl py-1.5 text-center leading-tight ${
                                    d.i === PICK.i ? "bg-lp-ink text-lp-paper" : "bg-lp-ink/[0.06] text-lp-ink/60"
                                 }`}
                              >
                                 <span className="block text-[11px] font-semibold capitalize">{d.i < 2 ? d.label : d.short}</span>
                                 <span className="block text-[9px] opacity-70">{d.date}</span>
                              </span>
                           ))}
                        </div>
                        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                           {["18:00", "18:30", "19:00"].map((t) => (
                              <span
                                 key={t}
                                 className={`rounded-xl py-2 text-center text-[12px] font-semibold ${
                                    t === "18:30" ? "bg-lp-ink text-lp-paper" : "bg-lp-ink/[0.06] text-lp-ink/60"
                                 }`}
                              >
                                 {t}
                              </span>
                           ))}
                        </div>
                        <div className="mt-4 flex items-center justify-between border-t border-dashed border-lp-ink/15 pt-3 text-[13px]">
                           <span className="text-lp-muted">Totaal</span>
                           <span className="font-semibold tabular-nums text-lp-ink">{euro(HERO_ORDER.total)}</span>
                        </div>
                        <span className="relative mt-4 flex items-center justify-center gap-2 rounded-2xl bg-lp-blue py-3.5 text-[13px] font-semibold text-white">
                           Betaal met Bancontact
                           <TapPulse on={phase === 4} />
                        </span>
                     </motion.div>
                  </motion.div>
               )}
            </AnimatePresence>

            {/* betaald */}
            <AnimatePresence>
               {phase === 5 && (
                  <motion.div
                     className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-lp-blue px-8 text-center text-white"
                     initial={{ clipPath: "circle(0% at 50% 85%)" }}
                     animate={{ clipPath: "circle(150% at 50% 85%)" }}
                     exit={{ opacity: 0 }}
                     transition={{ duration: 0.8, ease: EASE }}
                  >
                     <svg viewBox="0 0 52 52" className="h-16 w-16">
                        <motion.circle cx="26" cy="26" r="24" fill="none" stroke="white" strokeWidth="2.5"
                           initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.6, delay: 0.3 }} />
                        <motion.path d="M15 27 l7 7 l15 -16" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
                           initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.8 }} />
                     </svg>
                     <p className="mt-6 font-editorial text-[34px] italic leading-none">Betaald!</p>
                     <p className="mt-3 text-[13px] text-white/75">
                        Bestelling #{HERO_ORDER.number} · afhalen {PICK.long} om 18:30
                     </p>
                  </motion.div>
               )}
            </AnimatePresence>
         </div>
      </div>
   );
}

/* Telefoon + de bestelling die binnenkomt, met wat parallax op de muis */
function HeroStage() {
   const ref = useRef(null);
   const inView = useInView(ref, { margin: "-10% 0px" });
   const reduce = useReducedMotion();
   const introDone = useIntroDone();
   const [phase, setPhase] = useState(0);

   useEffect(() => {
      if (reduce || !inView || !introDone) return;
      const t = setTimeout(() => setPhase((p) => (p + 1) % PHASE_MS.length), PHASE_MS[phase]);
      return () => clearTimeout(t);
   }, [phase, inView, reduce, introDone]);

   const shownPhase = reduce ? 2 : phase;
   const printing = reduce || phase === 5;

   const mx = useMotionValue(0);
   const my = useMotionValue(0);
   const sx = useSpring(mx, { stiffness: 60, damping: 18 });
   const sy = useSpring(my, { stiffness: 60, damping: 18 });
   const phoneX = useTransform(sx, (v) => v * 14);
   const phoneY = useTransform(sy, (v) => v * 10);
   const ticketX = useTransform(sx, (v) => v * -26);
   const ticketY = useTransform(sy, (v) => v * -18);

   useEffect(() => {
      const onMove = (e) => {
         mx.set(e.clientX / window.innerWidth - 0.5);
         my.set(e.clientY / window.innerHeight - 0.5);
      };
      window.addEventListener("pointermove", onMove);
      return () => window.removeEventListener("pointermove", onMove);
   }, [mx, my]);

   return (
      <div ref={ref} className="relative mx-auto h-[420px] w-full max-w-[520px] sm:h-[640px]">
         <div className="absolute left-1/2 top-0 h-[640px] w-[520px] origin-top -translate-x-1/2 scale-[0.64] sm:scale-100">
            <motion.div
               className="absolute right-0 top-0"
               style={{ x: phoneX, y: phoneY }}
               initial={{ opacity: 0, y: 80, rotate: 6 }}
               animate={introDone ? { opacity: 1, y: 0, rotate: 3 } : {}}
               transition={{ duration: 1.3, ease: EASE, delay: 0.35 }}
            >
               <HeroPhone phase={shownPhase} />
            </motion.div>

            <motion.div
               className="absolute left-0 top-[180px] z-20 w-[248px]"
               style={{ x: ticketX, y: ticketY }}
               initial={{ opacity: 0, x: -40, rotate: -10 }}
               animate={introDone ? { opacity: 1, x: 0, rotate: -4 } : {}}
               transition={{ duration: 1.2, ease: EASE, delay: 0.55 }}
            >
               <TicketPrinter printing={printing} label="Nieuwe bestellingen">
                  <KitchenTicket order={HERO_ORDER} stamp={printing} stampDelay={1.5} />
               </TicketPrinter>
            </motion.div>

            {/* melding */}
            <AnimatePresence>
               {printing && !reduce && (
                  <motion.div
                     initial={{ opacity: 0, y: -16, scale: 0.9 }}
                     animate={{ opacity: 1, y: 0, scale: 1 }}
                     exit={{ opacity: 0, y: -10, scale: 0.95 }}
                     transition={{ duration: 0.5, ease: EASE, delay: 0.2 }}
                     className="absolute left-6 top-14 z-30 flex items-center gap-3 rounded-2xl bg-white/90 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(18,17,16,0.45)] backdrop-blur"
                  >
                     <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-lp-blue text-white">
                        <ShoppingBag size={16} />
                     </span>
                     <span className="leading-tight">
                        <span className="block text-[13px] font-semibold text-lp-ink">Nieuwe bestelling</span>
                        <span className="block text-[11px] text-lp-muted">
                           {euro(HERO_ORDER.total)} · afhalen {PICK.long} 18:30
                        </span>
                     </span>
                  </motion.div>
               )}
            </AnimatePresence>
         </div>
      </div>
   );
}

/* ------------------------------------------------------------------ */
/*  Woord dat wisselt: "voor je brasserie / restaurant / …"            */
/* ------------------------------------------------------------------ */

function WordRotator({ words }) {
   const [i, setI] = useState(0);
   const reduce = useReducedMotion();
   useEffect(() => {
      if (reduce) return;
      const t = setInterval(() => setI((n) => (n + 1) % words.length), 2200);
      return () => clearInterval(t);
   }, [words.length, reduce]);

   return (
      <span className="relative inline-flex overflow-hidden align-bottom">
         <AnimatePresence mode="wait" initial={false}>
            <motion.span
               key={words[i]}
               initial={{ y: "100%" }}
               animate={{ y: "0%" }}
               exit={{ y: "-100%" }}
               transition={{ duration: 0.45, ease: EASE }}
               className="inline-block font-editorial text-[1.2em] italic leading-none text-lp-blue"
            >
               {words[i]}
            </motion.span>
         </AnimatePresence>
      </span>
   );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */

export default function Hero() {
   const introDone = useIntroDone();
   const ref = useRef(null);
   const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
   const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);
   const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

   const facts = ["0% commissie", "Bancontact & kaart", "Je eigen huisstijl", "Online in één namiddag"];

   return (
      <section ref={ref} className="lp-grain relative overflow-hidden bg-lp-paper pt-32 sm:pt-40">
         {/* redactionele kolomlijnen */}
         <div aria-hidden className="pointer-events-none absolute inset-0 mx-auto grid max-w-[1400px] grid-cols-4 px-5 sm:px-8">
            {[0, 1, 2, 3].map((c) => (
               <span key={c} className="border-l border-lp-ink/[0.06] last:border-r" />
            ))}
         </div>
         {/* een zachte blauwe gloed */}
         <div aria-hidden className="pointer-events-none absolute -right-40 top-10 h-[620px] w-[620px] rounded-full bg-lp-blue/[0.10] blur-[140px]" />

         <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
            <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-6">
               <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7 lg:pt-6">
                  {/* De H1 draagt de zoektermen; de grote kop eronder is beeld en wordt niet voorgelezen */}
                  <motion.h1
                     initial={{ opacity: 0, y: 10 }}
                     animate={introDone ? { opacity: 1, y: 0 } : {}}
                     transition={{ duration: 0.8, ease: EASE }}
                     className="flex items-center gap-2.5 font-ticket text-[11px] font-normal uppercase tracking-[0.2em] text-lp-muted"
                  >
                     <span className="relative flex h-2 w-2">
                        <span className="absolute inset-0 animate-ping rounded-full bg-lp-blue/60" />
                        <span className="relative h-2 w-2 rounded-full bg-lp-blue" />
                     </span>
                     Online bestelsysteem voor de horeca
                     <span className="sr-only">: bestellingen binnen, gedoe buiten.</span>
                  </motion.h1>

                  <MaskLines
                     aria-hidden
                     play={introDone}
                     delay={0.1}
                     stagger={0.11}
                     className="mt-6 text-[clamp(3rem,6.6vw,7rem)] font-semibold leading-[0.9] tracking-[-0.05em] text-lp-ink"
                     lines={[
                        "Bestellingen",
                        <span className="inline-flex items-center gap-[0.18em]" aria-hidden>
                           binnen,
                           <span className="relative inline-block h-[0.74em] w-[1.55em] overflow-hidden rounded-full">
                              <motion.img
                                 src={kitchenPhoto}
                                 alt=""
                                 className="absolute inset-0 h-full w-full object-cover"
                                 initial={{ scale: 1.5 }}
                                 animate={introDone ? { scale: 1 } : {}}
                                 transition={{ duration: 2, ease: EASE, delay: 0.4 }}
                              />
                           </span>
                        </span>,
                        <span aria-hidden>
                           gedoe <Em className="text-lp-blue">buiten.</Em>
                        </span>,
                     ]}
                  />

                  <motion.p
                     initial={{ opacity: 0, y: 16 }}
                     animate={introDone ? { opacity: 1, y: 0 } : {}}
                     transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
                     className="mt-8 max-w-[34rem] text-lg leading-relaxed text-lp-muted sm:text-xl"
                  >
                     Een eigen webshop voor je <WordRotator words={BUSINESS_TYPES} />
                     <br />
                     Gasten bestellen vooraf, kiezen zelf hun dag en uur en betalen online. In je eigen
                     stijl, zonder commissie op je bestellingen.
                  </motion.p>

                  <motion.div
                     initial={{ opacity: 0, y: 16 }}
                     animate={introDone ? { opacity: 1, y: 0 } : {}}
                     transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                     className="mt-10 flex flex-wrap items-center gap-3"
                  >
                     <PillButton href={CONTACT_MAIL} size="lg">
                        Vraag een demo
                     </PillButton>
                     <PillButton variant="ghost" size="lg" onClick={() => scrollToTarget("#demo")}>
                        Speel zelf klant
                     </PillButton>
                  </motion.div>
               </motion.div>

               <div className="lg:col-span-5">
                  <HeroStage />
               </div>
            </div>

            {/* feitenbalk */}
            <motion.div
               initial={{ opacity: 0 }}
               animate={introDone ? { opacity: 1 } : {}}
               transition={{ duration: 1, delay: 1 }}
               className="mt-6 flex flex-col gap-6 border-t border-lp-ink/10 py-6 sm:mt-10 md:flex-row md:items-center md:justify-between"
            >
               <ul className="grid grid-cols-2 gap-x-6 gap-y-2 font-ticket text-[11px] uppercase tracking-[0.16em] text-lp-ink/70 md:flex md:gap-10">
                  {facts.map((f) => (
                     <li key={f} className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-lp-blue" />
                        {f}
                     </li>
                  ))}
               </ul>
               <button
                  onClick={() => scrollToTarget("#idee")}
                  className="group hidden items-center gap-3 font-ticket text-[11px] uppercase tracking-[0.16em] text-lp-muted md:flex"
               >
                  Scroll
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border border-lp-ink/15 transition-colors group-hover:border-lp-ink group-hover:bg-lp-ink group-hover:text-lp-paper">
                     <motion.span animate={{ y: [0, 3, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
                        <ChevronDown size={16} />
                     </motion.span>
                  </span>
               </button>
            </motion.div>
         </div>
      </section>
   );
}
